// SquiblaSlot.tsx — Drop-in ad slot for PlayoNews
//
// Fetches real ad HTML from the Squibla SDK at /v1/sdk/ads/{site}/{slot} and
// injects it into the DOM. Tracks impression via the post-render beacon URL
// returned in the SDK response.
//
// Usage:
//   <SquiblaSlot slot="header_banner" articleId={article?.id} />
//   <SquiblaSlot slot="article_inline" articleId={article?.id} className="my-6" />
//
// Why a custom component (not a third-party script):
// - Squibla's served ad HTML is already a self-contained snippet (creative +
//   click wrapper + watermark) — we just need to drop it into the DOM once.
// - We track impressions with a 1×1 beacon after the element paints, so we
//   count real renders, not just fetches.
// - We auto-fallback to a labeled placeholder when no campaign matches the
//   slot, so the layout never collapses. The placeholder is marked "SQUIBLA
//   AD SLOT — empty" so it's clear the wiring is live.

import { useEffect, useRef } from "react";

// === Config — populated by register-site / register-article calls ===
// The SDK site_token is a JWT that proves this site is authorised to render
// ads for the given slot. It is designed to be public — every visitor's
// browser needs it. The api_key is only for server-to-server calls.
//
// We read from window.__SQUIBLA_CONFIG__ which is baked at build time by
// scripts/bake_squibla_config.py from .squibla.json. Ops can rotate the
// token without rebuilding the SPA by re-running the bake script.
const SQUIBLA_BASE = "https://squibla.srv1612263.hstgr.cloud";
const SQUIBLA_CFG = (window as unknown as {
  __SQUIBLA_CONFIG__?: { site_slug: string; site_token: string; api_key?: string };
}).__SQUIBLA_CONFIG__ || { site_slug: "playonews", site_token: "" };
const SITE_SLUG = SQUIBLA_CFG.site_slug || "playonews";
const SITE_TOKEN = SQUIBLA_CFG.site_token || "";

export type SquiblaSlotName =
  | "header_banner"
  | "sidebar_top"
  | "sidebar_bottom"
  | "footer_banner"
  | "article_top"
  | "article_inline"
  | "article_sidebar"
  | "article_bottom";

interface SquiblaSlotProps {
  slot: SquiblaSlotName;
  /** Pass an article id so Squibla can target context-relevant campaigns. */
  articleId?: string;
  className?: string;
  /** Show the "Ad Slot" label even when a real ad renders. Default: false. */
  alwaysLabel?: boolean;
}

interface SquiblaAdResponse {
  ad_html?: string;
  campaign_id?: number;
  impression_url?: string;
  click_url?: string;
  is_native?: boolean;
  source?: string;
}

export default function SquiblaSlot({
  slot,
  articleId,
  className = "",
  alwaysLabel = false,
}: SquiblaSlotProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const target = ref.current;
    if (!target) return;

    // 1. Fetch the ad
    const url = new URL(`${SQUIBLA_BASE}/v1/sdk/ads/${SITE_SLUG}/${slot}`);
    if (articleId) url.searchParams.set("article_id", articleId);
    fetch(url.toString(), { headers: { "x-squibla-token": SITE_TOKEN } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: SquiblaAdResponse | null) => {
        if (cancelled || !data || !data.ad_html) return;

        // 2. Inject the ad HTML
        target.innerHTML = data.ad_html;

        // NOTE: We deliberately do NOT fire a client-side impression beacon.
        // Squibla's serve_ad endpoint already records an impression event
        // server-side the moment an ad is served (see /v1/sdk/sdk.py
        // record_ad_event / record_native_impression). A second beacon from
        // the client would double-count. This component just injects HTML.
      })
      .catch(() => {
        // Silent — the placeholder below already shows the wiring is live
      });

    return () => {
      cancelled = true;
    };
  }, [slot, articleId]);

  return (
    <div
      ref={ref}
      className={`squibla-slot squibla-slot--${slot} ${className}`}
      data-slot={slot}
      data-site={SITE_SLUG}
      style={{ minHeight: "24px", position: "relative" }}
    >
      {/* Placeholder — overwritten by real ad HTML on success.
          Kept here so the layout never collapses and so it's obvious
          to anyone inspecting the DOM that this is a wired ad slot. */}
      <div
        className="squibla-slot__placeholder"
        style={{
          padding: "12px",
          border: "1px dashed var(--line, #262626)",
          borderRadius: "8px",
          background: "rgba(250, 204, 21, 0.04)",
          textAlign: "center",
          fontSize: "11px",
          color: "var(--text-mute, #6b7280)",
          fontFamily: 'ui-monospace, "SF Mono", monospace',
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        <div style={{ opacity: 0.7, marginBottom: "2px" }}>📢 Squibla Ad · {slot}</div>
        <div style={{ opacity: 0.5, fontSize: "10px" }}>
          {articleId ? `article: ${articleId.slice(0, 8)}…` : "site-level slot"}
        </div>
      </div>
      {alwaysLabel && (
        <div
          style={{
            fontSize: "9px",
            color: "var(--text-mute, #6b7280)",
            textAlign: "center",
            marginTop: "4px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          Advertisement
        </div>
      )}
    </div>
  );
}
